import { useCallback, useState } from 'react';
import { DayPicker, SelectSingleEventHandler } from 'react-day-picker';

import useOutsideClick from '@cobalt/react-outside-click-hook';

import WfInput from '../wf-input';
import { CalendarSvg, ClearSvg } from '../wf-svg';

import { WebformLanguage, WebformLanguageMapper } from '@/models/web-form.model';

import 'react-day-picker/dist/style.css';
import './index.scss';

export interface WfDatePickerProps {
  value: string;
  variation?: 'error';
  placeholder?: string;
  language: WebformLanguage;
  onChange: (day: Date | undefined) => void;
  onBlur?: () => void;
}

const WfDatePicker: React.FC<WfDatePickerProps> = (props) => {
  const { value = '', variation, placeholder, language: _language, onChange, onBlur } = props;

  const curLocale = WebformLanguageMapper[_language].locale;

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date>();
  const [month, setMonth] = useState<Date>();

  const outsideClickHandler = useCallback(() => {
    if (!open) return;
    setMonth(value ? new Date(value) : undefined);
    onBlur?.();
    setOpen(false);
  }, [onBlur, open, value]);
  const { onClickCapture } = useOutsideClick(outsideClickHandler);

  const handleClear = useCallback(() => {
    onChange(undefined);
    setDate(undefined);
    setMonth(undefined);
    setOpen(false);
  }, [onChange]);

  const handleSelect = useCallback<SelectSingleEventHandler>(
    (day) => {
      setDate(day);
      setMonth(day);
      onChange(day);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <div className="wf-date-picker-container" onClick={onClickCapture}>
      <WfInput
        className="wf-date-picker-input"
        value={value}
        readOnly
        variation={variation}
        placeholder={placeholder}
        focused={open}
        onClick={() => setOpen(true)}
      />

      <div className="wf-date-picker-icon-container">
        {!!value && <ClearSvg className="wf-date-picker-clear" onClick={handleClear} />}

        <CalendarSvg className="wf-date-picker-calendar" onClick={() => setOpen(true)} />
      </div>

      {open && (
        <div className="wf-date-picker-wrapper">
          <DayPicker
            locale={curLocale}
            mode="single"
            captionLayout="dropdown-buttons"
            selected={date}
            month={month}
            showOutsideDays
            weekStartsOn={0}
            fromYear={1920}
            toYear={2100}
            onSelect={handleSelect}
            onMonthChange={setMonth}
          />
        </div>
      )}
    </div>
  );
};

export default WfDatePicker;
